/**
 * Sleep Reset Academy — bilingual UI copy.
 * Kept in a single file for Phase 1.5 to avoid touching the platform-wide
 * translation infra. Migrate into /translations/ later if needed.
 */
export const getAcademyCopy = (language) => {
  const isEs = language === 'es';
  return {
    // Nav / branding
    academyName: 'Sleep Reset Academy',
    academyKicker: isEs
      ? 'Estructura de aprendizaje del sueño'
      : 'Structured sleep learning',

    // Homepage
    heroTagline: isEs
      ? 'Aprende sobre salud del sueño con lecciones cortas y bilingües, ordenadas de principiante a técnico avanzado.'
      : 'Learn sleep health through short, bilingual lessons — sequenced from beginner to advanced technologist.',
    startBeginner: isEs ? 'Empezar la Ruta Principiante' : 'Start the Beginner Path',
    continueLearning: isEs ? 'Continuar Aprendiendo' : 'Continue Learning',
    resumeAt: isEs ? 'Continuar en:' : 'Resume at:',
    yourProgress: isEs ? 'Tu progreso' : 'Your progress',
    activePathLabel: isEs ? 'Ruta activa' : 'Active path',
    complete: isEs ? 'Completado' : 'Complete',
    lessonsShort: isEs ? 'lecciones' : 'lessons',
    minShort: 'min',
    modulesHeader: isEs ? 'Módulos del Currículo' : 'Curriculum Modules',
    modulesSub: isEs
      ? '12 módulos secuenciados. Cada lección es una presentación completa (Leer · Diapositivas · Escuchar) en inglés y español; los videos cortos son material complementario.'
      : '12 sequenced modules. Every lesson is a full presentation (Read · Slides · Listen) in English and Spanish; short videos are supplementary.',
    pathsHeader: isEs ? 'Rutas de Aprendizaje' : 'Learning Paths',
    pathsSub: isEs
      ? 'Elige una ruta que se adapte a tu objetivo.'
      : 'Choose the path that fits your goal.',
    prerequisiteLabel: isEs ? 'Requisito:' : 'Prerequisite:',
    noPrerequisite: isEs ? 'Sin requisitos' : 'No prerequisites',
    startPath: isEs ? 'Comenzar ruta' : 'Start path',
    viewPath: isEs ? 'Ver ruta' : 'View path',
    comingSoon: isEs ? 'Próximamente' : 'Coming soon',
    available: isEs ? 'Disponible' : 'Available',
    inProgress: isEs ? 'En progreso' : 'In progress',

    // Module page
    lessonCount: (n) => (isEs ? `${n} lecciones` : `${n} lessons`),
    modulePriority: isEs ? 'Prioridad' : 'Priority',
    backToAcademy: isEs ? '← Academia' : '← Academy',
    backToPath: isEs ? '← Ruta' : '← Path',

    // Path page
    pathLessonsSummary: (n, mins) => {
      const nn = Number(n) || 0;
      const mm = Number(mins) || 0;
      const lessonEs = nn === 1 ? 'lección' : 'lecciones';
      const lessonEn = nn === 1 ? 'lesson' : 'lessons';
      return isEs
        ? `${nn} ${lessonEs} · aproximadamente ${mm} min de video`
        : `${nn} ${lessonEn} · approximately ${mm} min of video`;
    },

    // Lesson player
    watchLesson: isEs ? 'Ver lección' : 'Watch lesson',
    markComplete: isEs ? 'Marcar como completada' : 'Mark as complete',
    completed: isEs ? '✓ Completada' : '✓ Completed',
    prevLesson: isEs ? 'Anterior' : 'Previous',
    nextLesson: isEs ? 'Siguiente' : 'Next',
    lessonNotAvailable: isEs
      ? 'Esta lección aún no está disponible.'
      : 'This lesson is not available yet.',
    languageToggleEn: 'English',
    languageToggleEs: 'Español',

    // Disclaimer (reused wording aligned with HomePage.jsx canonical footer)
    disclaimerTitle: isEs ? 'Solo educativo' : 'Educational only',
    disclaimerBody: isEs
      ? 'Sleep Health Services proporciona información educativa y de bienestar únicamente. Este contenido no constituye consejo médico, diagnóstico ni tratamiento, y su uso no crea una relación médico-paciente. Para asuntos médicos, consulte a un profesional de la salud con licencia.'
      : 'Sleep Health Services provides educational and wellness information only. This content does not constitute medical advice, diagnosis, or treatment, and no physician-patient relationship is created by your use of this platform. For medical concerns, consult a licensed healthcare professional.',

    // Empty states
    noLessonsYet: isEs
      ? 'Este módulo aún no tiene lecciones publicadas.'
      : 'No lessons are published in this module yet.',
    loading: isEs ? 'Cargando…' : 'Loading…',
    error: isEs
      ? 'No pudimos cargar la academia. Intenta de nuevo.'
      : 'Could not load the academy. Please try again.',

    // ─── Phase 2 ───────────────────────────────────────────────
    // Dashboard
    dashboard: isEs ? 'Panel del Estudiante' : 'Student Dashboard',
    dashboardTagline: isEs
      ? 'Tu progreso, tu ritmo, tu camino en Sleep Reset Academy.'
      : 'Your progress, your pace, your journey in Sleep Reset Academy.',
    overallProgress: isEs ? 'Progreso general' : 'Overall progress',
    lessonsCompleted: isEs ? 'Lecciones completadas' : 'Lessons completed',
    modulesCompleted: isEs ? 'Módulos completados' : 'Modules completed',
    pathsCompleted: isEs ? 'Rutas completadas' : 'Paths completed',
    studyHours: isEs ? 'Horas de estudio' : 'Study hours',
    currentStreak: isEs ? 'Racha actual' : 'Current streak',
    dayStreak: (n) => (isEs
      ? `${n} ${n === 1 ? 'día seguido' : 'días seguidos'}`
      : `${n} day${n === 1 ? '' : 's'}`),
    nextLessonHeader: isEs ? 'Tu próxima lección recomendada' : 'Your next recommended lesson',
    resumeButton: isEs ? 'Continuar' : 'Resume',
    recentlyViewed: isEs ? 'Vistas recientemente' : 'Recently viewed',
    noRecent: isEs ? 'Aún no has visto ninguna lección.' : 'No lessons viewed yet.',
    modulesProgressHeader: isEs ? 'Progreso por módulo' : 'Progress by module',
    pathsProgressHeader: isEs ? 'Progreso por ruta' : 'Progress by path',

    // Profile
    profile: isEs ? 'Perfil' : 'Profile',
    profileTagline: isEs
      ? 'Tu identidad como aprendiz de Sleep Reset Academy.'
      : 'Your learner identity in Sleep Reset Academy.',
    learnerName: isEs ? 'Nombre del estudiante' : 'Learner name',
    learnerNamePlaceholder: isEs ? 'Tu nombre completo' : 'Your full name',
    learnerNameHint: isEs
      ? 'Este nombre aparecerá en los certificados de finalización.'
      : 'This name will appear on your completion certificates.',
    save: isEs ? 'Guardar' : 'Save',
    saved: isEs ? '¡Guardado!' : 'Saved!',
    badgesHeader: isEs ? 'Insignias' : 'Badges',
    badgesUnlocked: isEs ? 'Desbloqueadas' : 'Unlocked',
    certificatesHeader: isEs ? 'Certificados' : 'Certificates',
    noCertificatesYet: isEs
      ? 'Completa una ruta para ganar tu primer certificado.'
      : 'Complete a learning path to earn your first certificate.',
    viewCertificate: isEs ? 'Ver certificado' : 'View certificate',
    unlocked: isEs ? 'Desbloqueada' : 'Unlocked',
    locked: isEs ? 'Bloqueada' : 'Locked',
    resetProgress: isEs ? 'Reiniciar todo el progreso' : 'Reset all progress',
    resetConfirm: isEs
      ? '¿Reiniciar todo el progreso, certificados e insignias?'
      : 'Reset all progress, certificates and badges?',

    // Certificate
    certificateTitle: isEs ? 'Certificado de Finalización' : 'Certificate of Completion',
    certificateBody: isEs ? 'Se otorga a' : 'This is presented to',
    certificateForPath: isEs
      ? 'por completar exitosamente la'
      : 'for successfully completing the',
    onDate: isEs ? 'el' : 'on',
    certificateId: isEs ? 'ID del Certificado' : 'Certificate ID',
    programDirector: isEs ? 'Director del Programa' : 'Program Director',
    medicalReviewer: isEs ? 'Revisor Médico' : 'Medical Reviewer',
    signaturePending: isEs ? 'Firma tras revisión clínica' : 'Signature upon clinical review',
    scanToVerify: isEs ? 'Escanea para verificar' : 'Scan to verify',
    downloadPdf: isEs ? 'Descargar PDF' : 'Download PDF',
    downloadImage: isEs ? 'Descargar imagen' : 'Download image',
    lessonsWord: isEs ? 'lecciones' : 'lessons',
    hoursWord: isEs ? 'horas' : 'hours',
    poweredBy: isEs ? 'Emitido por' : 'Issued by',
    certificateEarned: isEs ? '¡Certificado obtenido!' : 'Certificate earned!',
    viewYourCertificate: isEs ? 'Ver tu certificado' : 'View your certificate',
    // Dashboard nav
    goToDashboard: isEs ? 'Panel' : 'Dashboard',
    goToProfile: isEs ? 'Perfil' : 'Profile',
    backToDashboard: isEs ? '← Panel' : '← Dashboard',

    // ─── Phase 3 ───────────────────────────────────────────────
    // Nav pills
    planner: isEs ? 'Planificador' : 'Planner',
    achievements: isEs ? 'Logros' : 'Achievements',
    transcript: isEs ? 'Expediente' : 'Transcript',
    leaderboard: isEs ? 'Clasificación' : 'Leaderboard',
    settings: isEs ? 'Ajustes' : 'Settings',
    favorites: isEs ? 'Favoritos' : 'Favorites',
    bookmarks: isEs ? 'Marcadores' : 'Bookmarks',
    search: isEs ? 'Buscar' : 'Search',
    notes: isEs ? 'Notas' : 'Notes',
    share: isEs ? 'Compartir' : 'Share',
    bookmark: isEs ? 'Marcar' : 'Bookmark',
    bookmarked: isEs ? 'Marcado' : 'Bookmarked',
    favorite: isEs ? 'Favorito' : 'Favorite',
    favorited: isEs ? 'En favoritos' : 'Favorited',
    learningObjectives: isEs ? 'Objetivos de aprendizaje' : 'Learning objectives',
    difficulty: isEs ? 'Dificultad' : 'Difficulty',

    // Study planner
    plannerTitle: isEs ? 'Planificador de estudio' : 'Study Planner',
    plannerTagline: isEs
      ? 'Elige tu ritmo. Estimaremos cuándo terminarás.'
      : 'Choose your pace. We\'ll estimate when you\'ll finish.',
    plan15: isEs ? '15 minutos / día' : '15 minutes / day',
    plan30: isEs ? '30 minutos / día' : '30 minutes / day',
    plan60: isEs ? '1 hora / día' : '1 hour / day',
    planCustom: isEs ? 'Personalizado' : 'Custom',
    customMinutes: isEs ? 'Minutos por día' : 'Minutes per day',
    savePlan: isEs ? 'Guardar plan' : 'Save plan',
    clearPlan: isEs ? 'Reiniciar plan' : 'Clear plan',
    weeklyGoal: isEs ? 'Meta semanal' : 'Weekly goal',
    expectedCompletion: isEs ? 'Fecha estimada de finalización' : 'Expected completion date',
    remainingHours: isEs ? 'Horas restantes' : 'Remaining hours',
    remainingLessons: isEs ? 'Lecciones restantes' : 'Remaining lessons',
    plannerActive: isEs ? 'Plan activo:' : 'Active plan:',
    minutesPerDay: (n) => (isEs ? `${n} min / día` : `${n} min / day`),

    // Achievement center
    achievementsTitle: isEs ? 'Centro de Logros' : 'Achievement Center',
    achievementsTagline: isEs
      ? 'Todo lo que has ganado en un solo lugar.'
      : 'Everything you\'ve earned, in one place.',
    milestones: isEs ? 'Hitos' : 'Milestones',
    milestone1stLesson: isEs ? 'Primera lección' : 'First lesson',
    milestone5Lessons: isEs ? '5 lecciones' : '5 lessons',
    milestone10Lessons: isEs ? '10 lecciones' : '10 lessons',
    milestone25Lessons: isEs ? '25 lecciones' : '25 lessons',
    milestone1stCertificate: isEs ? 'Primer certificado' : 'First certificate',
    milestoneFirstModule: isEs ? 'Primer módulo' : 'First module',
    milestoneFirstPath: isEs ? 'Primera ruta' : 'First path',
    milestone7DayStreak: isEs ? 'Racha de 7 días' : '7-day streak',

    // Search
    searchTitle: isEs ? 'Buscar Academia' : 'Search Academy',
    searchPlaceholder: isEs ? 'Buscar lecciones, módulos, rutas…' : 'Search lessons, modules, paths…',
    filterModule: isEs ? 'Módulo' : 'Module',
    filterPath: isEs ? 'Ruta' : 'Path',
    filterDifficulty: isEs ? 'Dificultad' : 'Difficulty',
    filterAll: isEs ? 'Todos' : 'All',
    searchNoResults: isEs ? 'Sin resultados. Intenta con otras palabras.' : 'No results. Try different keywords.',
    searchResults: (n) => (isEs ? `${n} resultado${n === 1 ? '' : 's'}` : `${n} result${n === 1 ? '' : 's'}`),

    // Favorites / Bookmarks page
    favoritesTitle: isEs ? 'Tus Favoritos' : 'Your Favorites',
    bookmarksTitle: isEs ? 'Marcadores' : 'Bookmarks',
    noFavorites: isEs
      ? 'Aún no has agregado favoritos. Marca lecciones con el ícono de estrella.'
      : 'No favorites yet. Star a lesson to add it here.',
    noBookmarks: isEs
      ? 'Aún no has agregado marcadores. Marca lecciones para reanudarlas fácilmente.'
      : 'No bookmarks yet. Bookmark a lesson to resume it easily.',

    // Transcript
    transcriptTitle: isEs ? 'Expediente Académico' : 'Academic Transcript',
    transcriptTagline: isEs
      ? 'Registro completo de tu recorrido en Sleep Reset Academy.'
      : 'Complete record of your Sleep Reset Academy journey.',
    downloadTranscript: isEs ? 'Descargar expediente' : 'Download transcript',
    coursesWord: isEs ? 'Cursos' : 'Courses',
    ceuHeader: isEs ? 'Créditos de Educación Continua (CEUs)' : 'Continuing Education Credits (CEUs)',
    ceuPlaceholder: isEs
      ? 'CEUs — próximamente. Los créditos de educación continua estarán disponibles cuando Sleep Reset Academy obtenga la acreditación correspondiente. Tu progreso actual será elegible retroactivamente cuando el marco esté activo.'
      : 'CEUs — coming soon. Continuing education credits will be available once Sleep Reset Academy obtains formal accreditation. Your current progress will be eligible retroactively when the framework goes live.',
    comingSoonBadge: isEs ? 'Próximamente' : 'Coming Soon',

    // Leaderboard
    leaderboardTitle: isEs ? 'Clasificación' : 'Leaderboard',
    leaderboardTagline: isEs
      ? 'Tu progreso en Sleep Reset Academy. Compárate contigo mismo, semana a semana.'
      : 'Your progress in Sleep Reset Academy. Compare yourself with yourself, week by week.',
    leaderboardNote: isEs
      ? 'La clasificación es únicamente local (localStorage). Sleep Reset Academy no comparte tu progreso con otros usuarios.'
      : 'The leaderboard is local-only (localStorage). Sleep Reset Academy does not share your progress with other users.',
    rank: isEs ? 'Puesto' : 'Rank',

    // Settings
    settingsTitle: isEs ? 'Ajustes de la Academia' : 'Academy Settings',
    settingLanguage: isEs ? 'Idioma preferido' : 'Preferred language',
    settingDailyGoal: isEs ? 'Meta diaria de estudio (min)' : 'Daily study goal (min)',
    settingReminder: isEs ? 'Recordatorio diario' : 'Daily reminder',
    settingReminderHint: isEs
      ? 'Solo interfaz: no se envían notificaciones push (aún).'
      : 'UI-only: no push notifications sent (yet).',
    settingAutoplay: isEs ? 'Reproducción automática de video' : 'Video autoplay',
    on: isEs ? 'Activado' : 'On',
    off: isEs ? 'Desactivado' : 'Off',

    // Paths overview page
    overviewLabel: isEs ? 'Descripción' : 'Overview',
    objectivesLabel: isEs ? 'Objetivos' : 'Objectives',
    skillsLabel: isEs ? 'Habilidades' : 'Skills you\'ll gain',
    requirementsLabel: isEs ? 'Requisitos' : 'Requirements',
    estimatedDuration: isEs ? 'Duración estimada' : 'Estimated duration',

    // Module cert
    moduleCertificate: isEs ? 'Certificado del módulo' : 'Module certificate',
    academyCertificate: isEs ? 'Certificado de la Academia' : 'Academy certificate',

    // Homepage sections
    homeFeaturedPath: isEs ? 'Ruta destacada' : 'Featured Learning Path',
    homeRecommended: isEs ? 'Recomendado para ti' : 'Recommended for you',
    homeNewest: isEs ? 'Lecciones más recientes' : 'Newest lessons',
    homeSnapshot: isEs ? 'Resumen de progreso' : 'Progress snapshot',
    homeAchievements: isEs ? 'Tus logros' : 'Your achievements',

    // Lesson experience
    notesPlaceholder: isEs ? 'Tus notas personales sobre esta lección…' : 'Your personal notes for this lesson…',
    notesSaved: isEs ? 'Notas guardadas' : 'Notes saved',
    shareCopied: isEs ? '¡Enlace copiado!' : 'Link copied!',

    // Learning goals
    learningGoalsHeader: isEs ? 'Metas de aprendizaje' : 'Learning goals',
    learningGoalsPlaceholder: isEs
      ? '¿Qué quieres lograr en Sleep Reset Academy?'
      : 'What do you want to accomplish in Sleep Reset Academy?',

    // ─── Phase 4 ───────────────────────────────────────────────
    // XP + Levels
    xp: 'XP',
    levelWord: isEs ? 'Nivel' : 'Level',
    xpTotal: isEs ? 'XP totales' : 'Total XP',
    xpToNext: isEs ? 'para el siguiente nivel' : 'to next level',
    xpMax: isEs ? 'Nivel máximo alcanzado' : 'Max level reached',
    xpBreakdownHeader: isEs ? 'Desglose de XP' : 'XP Breakdown',
    xpFromLessons: isEs ? 'Lecciones' : 'Lessons',
    xpFromModules: isEs ? 'Módulos' : 'Modules',
    xpFromPaths: isEs ? 'Rutas' : 'Paths',
    xpFromCertificates: isEs ? 'Certificados' : 'Certificates',
    xpFromStreak: isEs ? 'Rachas' : 'Streaks',

    // Calendar
    calendarTitle: isEs ? 'Calendario de estudio' : 'Study Calendar',
    calendarTagline: isEs
      ? 'Cada día que completas una lección se resalta. Mantén la racha.'
      : 'Every day you complete a lesson lights up. Keep the streak going.',
    weekdaysShort: isEs
      ? ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    monthYear: (year, monthIdx) => new Date(year, monthIdx, 1).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { month: 'long', year: 'numeric' }),
    activeDays: isEs ? 'Días activos' : 'Active days',
    longestStreak: isEs ? 'Racha más larga' : 'Longest streak',
    timelineHeader: isEs ? 'Cronología de aprendizaje' : 'Learning Timeline',
    noHistoryYet: isEs ? 'Sin historial aún. Completa tu primera lección.' : 'No history yet. Complete your first lesson.',

    // Weekly goal
    weeklyGoalHeader: isEs ? 'Meta semanal' : 'Weekly goal',
    weeklyGoalProgress: (a, b) => isEs ? `${a} de ${b} min` : `${a} of ${b} min`,
    thisWeek: isEs ? 'Esta semana' : 'This week',

    // Graduation
    graduationDate: isEs ? 'Fecha estimada de graduación' : 'Estimated graduation date',
    graduationHint: isEs
      ? 'Basado en tu plan actual y el contenido restante.'
      : 'Based on your current plan and remaining content.',

    // Portfolio
    portfolioTitle: isEs ? 'Portfolio del Estudiante' : 'Student Portfolio',
    portfolioTagline: isEs
      ? 'Una vista pública de tu progreso en Sleep Reset Academy.'
      : 'A shareable view of your Sleep Reset Academy progress.',
    portfolioCopyLink: isEs ? 'Copiar enlace' : 'Copy link',
    portfolioLinkCopied: isEs ? '¡Enlace copiado!' : 'Link copied!',
    portfolioPrint: isEs ? 'Imprimir' : 'Print',
    portfolioMember: isEs ? 'Miembro desde' : 'Member since',
    portfolioAcademyProgress: isEs ? 'Progreso en la Academia' : 'Academy progress',
    portfolioAchievements: isEs ? 'Logros' : 'Achievements',
    portfolioViewingAsPublic: isEs
      ? 'Vista pública. Se muestran solo los datos que has ganado.'
      : 'Public view. Only earned data is shown.',

    // Module checklists
    moduleChecklistHeader: isEs ? 'Lista de verificación del módulo' : 'Module checklist',

    // Print
    printTranscript: isEs ? 'Imprimir expediente' : 'Print transcript',

    // ─── Phase 4.5 — Polish ───────────────────────────────────
    welcomeGoodMorning: isEs ? 'Buenos días' : 'Good morning',
    welcomeGoodAfternoon: isEs ? 'Buenas tardes' : 'Good afternoon',
    welcomeGoodEvening: isEs ? 'Buenas noches' : 'Good evening',
    welcomeBackFriendly: (name) => isEs ? `¡Bienvenido de vuelta, ${name}!` : `Welcome back, ${name}!`,
    welcomeFirstTime: (name) => isEs ? `Bienvenido a Sleep Reset Academy, ${name}` : `Welcome to Sleep Reset Academy, ${name}`,
    dailyRecommendation: isEs ? 'Recomendación de hoy' : 'Today\'s recommendation',
    weeklyGoalReminder: (mins) => isEs ? `Aún puedes lograr tu meta semanal — te faltan ${mins} min esta semana.` : `You can still hit your weekly goal — ${mins} min to go this week.`,
    streakCelebration: (days) => isEs ? `¡${days} ${days === 1 ? 'día seguido' : 'días seguidos'}! Sigue así.` : `${days}-day streak! Keep it going.`,
    levelUpCelebration: (level, name) => isEs ? `¡Alcanzaste el Nivel ${level} — ${name}!` : `You reached Level ${level} — ${name}!`,
    moduleCompleteCelebration: isEs ? '¡Módulo completado!' : 'Module complete!',
    pathCompleteCelebration: isEs ? '¡Ruta completada!' : 'Path complete!',
    academyStatsTitle: isEs ? 'Estadísticas de la Academia' : 'Academy Statistics',

    // Accessibility
    ariaCloseModal: isEs ? 'Cerrar' : 'Close',
    ariaCelebration: isEs ? 'Notificación de celebración' : 'Celebration notification',

    // ─── Sprint 30.2 — AcademyShell identity strip ────────────
    shellShsLabel: 'Sleep Health Services',
    shellAcademyLabel: 'Sleep Reset Academy',
    shellAriaLabel: isEs ? 'Ubicación en Sleep Reset Academy' : 'Sleep Reset Academy location',
    shellCrumb_academyHome: isEs ? 'Inicio' : 'Home',
    shellCrumb_dashboard: isEs ? 'Panel' : 'Dashboard',
    shellCrumb_profile: isEs ? 'Perfil' : 'Profile',
    shellCrumb_planner: isEs ? 'Planificador' : 'Planner',
    shellCrumb_favorites: isEs ? 'Favoritos' : 'Favorites',
    shellCrumb_search: isEs ? 'Buscar' : 'Search',
    shellCrumb_achievements: isEs ? 'Logros' : 'Achievements',
    shellCrumb_transcript: isEs ? 'Expediente' : 'Transcript',
    shellCrumb_leaderboard: isEs ? 'Clasificación' : 'Leaderboard',
    shellCrumb_settings: isEs ? 'Ajustes' : 'Settings',
    shellCrumb_portfolio: isEs ? 'Portafolio' : 'Portfolio',
    shellCrumb_calendar: isEs ? 'Calendario' : 'Calendar',
    shellCrumb_certifications: isEs ? 'Certificaciones' : 'Certifications',
    shellCrumb_certificate: isEs ? 'Certificado' : 'Certificate',
    shellCrumb_practice: isEs ? 'Práctica' : 'Practice',
    shellCrumb_mastery: isEs ? 'Maestría' : 'Mastery',
    shellCrumb_shsai: 'SHS AI',
    shellCrumb_director: isEs ? 'Director de programa' : 'Program Director',
    shellCrumb_path: isEs ? 'Ruta' : 'Path',
    shellCrumb_module: isEs ? 'Módulo' : 'Module',
    shellCrumb_lesson: isEs ? 'Lección' : 'Lesson',
    shellCrumb_about: isEs ? 'Acerca de' : 'About',

    // ─── Sprint 30.3 — Canonical /academy landing ─────────────
    landingSubNew: isEs
      ? 'Aprende salud del sueño con lecciones cortas y bilingües, secuenciadas de principiante a técnico avanzado.'
      : 'Learn sleep health through short, bilingual lessons — sequenced from beginner to advanced technologist.',
    landingSubReturning: (name) => isEs
      ? (name
          ? `Bienvenido de vuelta, ${name}. Retoma donde lo dejaste.`
          : 'Bienvenido de vuelta. Retoma donde lo dejaste.')
      : (name
          ? `Welcome back, ${name}. Pick up where you left off.`
          : 'Welcome back. Pick up where you left off.'),
    landingPrimaryStart: isEs ? 'Comenzar Sleep Reset' : 'Start Sleep Reset',
    landingSecondaryDashboard: isEs ? 'Abrir mi Panel' : 'Open my Dashboard',
    landingSecondaryMembership: isEs ? 'Ver Membresía' : 'See Membership',
    landingSecondaryCertPrep: isEs ? 'Preparación para Certificación' : 'Certification Preparation',
    landingCertPrepCard_title: isEs ? 'Preparación para Certificación' : 'Certification Preparation',
    landingCertPrepCard_body: isEs
      ? 'CPSGT, RPSGT y CCSH — con guías, práctica y maestría adaptativa.'
      : 'CPSGT, RPSGT, and CCSH — with guides, practice, and adaptive mastery.',
    landingCertPrepCard_cta: isEs ? 'Abrir el Centro de Certificación' : 'Open the Certification Center',
    landingPathPitchTitle: isEs ? 'La ruta de aprendizaje en 3 niveles' : 'The 3-Level Learning Path',
    landingPathPitchSubtitle: isEs
      ? 'Diez módulos en tres niveles — previsualiza cualquier módulo antes de comprometerte.'
      : '10 modules across 3 levels — preview any module before committing.',
    landingLevelLabel: isEs ? 'Nivel' : 'Level',
    landingModuleHighlights: isEs ? 'Módulos destacados' : 'Module highlights',
    landingToolsTitle: isEs ? 'Herramientas para tu aprendizaje' : 'Tools for your learning',
    landingToolsSubtitle: isEs
      ? 'Todo lo que necesitas para planear, practicar y progresar.'
      : 'Everything you need to plan, practice, and progress.',
    landingBottomTitle: isEs
      ? '¿Listo para comenzar tu recorrido en medicina del sueño?'
      : 'Ready to begin your sleep medicine journey?',
    landingBottomDesc: isEs
      ? 'Explora todas las rutas y módulos en Sleep Reset Academy.'
      : 'Explore all paths and modules in Sleep Reset Academy.',
    landingBottomCta: isEs ? 'Abrir Sleep Reset Academy' : 'Open Sleep Reset Academy',
    landingToolCard_practice: isEs ? 'Centro de Práctica' : 'Practice Center',
    landingToolCard_mastery: isEs ? 'Coach de Maestría' : 'Mastery Coach',
    landingToolCard_achievements: isEs ? 'Logros' : 'Achievements',
    landingToolCard_transcript: isEs ? 'Expediente' : 'Transcript',
    landingToolCard_planner: isEs ? 'Planificador' : 'Planner',
    landingToolCard_shsai: 'SHS AI',

    // ─── Sprint 30.4 — Path & Module Learning Journey ─────────
    journeyStateCompleted: isEs ? 'Completado' : 'Completed',
    journeyStateInProgress: isEs ? 'En curso' : 'In progress',
    journeyStateNext: isEs ? 'Siguiente' : 'Next up',
    journeyStateNotStarted: isEs ? 'Sin iniciar' : 'Not started',
    journeyStateComingSoon: isEs ? 'Próximamente' : 'Coming soon',
    journeyOverviewTitle: isEs ? 'Ruta de aprendizaje' : 'Learning journey',
    journeyPathModules: (done, total) => {
      const t = Number(total) || 0;
      return isEs
        ? (t === 1 ? `${done} de 1 módulo completado` : `${done} de ${t} módulos completados`)
        : (t === 1 ? `${done} of 1 module complete` : `${done} of ${t} modules complete`);
    },
    journeyModuleLessons: (done, total) => {
      const t = Number(total) || 0;
      return isEs
        ? (t === 1 ? `${done} de 1 lección completada` : `${done} de ${t} lecciones completadas`)
        : (t === 1 ? `${done} of 1 lesson complete` : `${done} of ${t} lessons complete`);
    },
    journeyProgressAriaPath: (pct) => isEs
      ? `Progreso de la ruta: ${pct} por ciento completo`
      : `Path progress: ${pct} percent complete`,
    journeyProgressAriaModule: (pct) => isEs
      ? `Progreso del módulo: ${pct} por ciento completo`
      : `Module progress: ${pct} percent complete`,
    journeyContinueWith: isEs ? 'Continuar con' : 'Continue with',
    journeyNextLessonEyebrow: isEs ? 'Siguiente lección' : 'Next up',

    // ─── Sprint 30.5 — Lesson Focus Mode ─────────────────────
    lessonFocusModuleProgress: (moduleId, done, total) => isEs
      ? `Módulo ${moduleId} · ${done} de ${total} lecciones`
      : `Module ${moduleId} · ${done} of ${total} lessons`,
    lessonFocusPathProgress: (pathKey, done, total) => {
      const key = String(pathKey || '').toLowerCase();
      const nameEs = key === 'beginner' ? 'Principiante'
        : key === 'cpsgt' ? 'CPSGT'
        : key === 'rpsgt' ? 'RPSGT'
        : key === 'ccsh' ? 'CCSH'
        : (pathKey || '');
      const nameEn = key === 'beginner' ? 'Beginner'
        : key === 'cpsgt' ? 'CPSGT'
        : key === 'rpsgt' ? 'RPSGT'
        : key === 'ccsh' ? 'CCSH'
        : (pathKey || '');
      return isEs
        ? `Ruta ${nameEs} · ${done} de ${total}`
        : `${nameEn} Path · ${done} of ${total}`;
    },
    lessonFocusCompleteEyebrow: isEs ? 'Marcar esta lección como completada' : 'Mark this lesson complete',
    lessonFocusCompletedBody: isEs
      ? 'Ya completaste esta lección. Puedes continuar con la siguiente.'
      : 'You have completed this lesson. Continue to the next one when you are ready.',
    lessonFocusIncompleteBody: isEs
      ? 'Cuando termines de ver el video y sientas que dominas el contenido, márcala como completada.'
      : 'When you have watched the video and feel confident with the material, mark this lesson complete.',
    lessonFocusUtilityAria: isEs ? 'Herramientas de lección' : 'Lesson utilities',

    // ─── Sprint 30.9A — Hypnogram educational visualization ─────
    hypnogramTitle: isEs ? 'Ejemplo ilustrativo · Etapas del sueño' : 'Illustrative example · Sleep stages',
    hypnogramSubtitle: isEs
      ? 'Cómo pueden transitar Vigilia, N1, N2, N3 y REM durante una noche típica.'
      : 'How Wake, N1, N2, N3 and REM may transition across a typical night.',
    hypnogramContext: isEs
      ? 'Ejemplo ilustrativo — no son datos de un paciente.'
      : 'Illustrative example — not patient data.',
    hypnogramSrSummary: isEs
      ? 'Hipnograma ilustrativo que muestra transiciones entre Vigilia, N1, N2, N3 y REM a lo largo de ciclos repetidos durante aproximadamente ocho horas. El sueño profundo (N3) predomina en la primera mitad de la noche y los periodos REM se hacen más largos hacia el amanecer.'
      : 'Illustrative hypnogram showing transitions among Wake, N1, N2, N3 and REM across repeated sleep cycles over approximately eight hours. Deep sleep (N3) dominates the first half of the night; REM periods lengthen toward morning.',
    hypnogramStageWake: isEs ? 'Vigilia' : 'Wake',
    hypnogramStageREM: 'REM',
    hypnogramStageN1: 'N1',
    hypnogramStageN2: 'N2',
    hypnogramStageN3: 'N3',

    // ─── Sprint 30.9B — Sleep Cycle educational visualization ─────
    sleepCycleTitle: isEs ? 'Ejemplo ilustrativo · Ciclo del sueño (~90 min)' : 'Illustrative example · Sleep cycle (~90 min)',
    sleepCycleSubtitle: isEs
      ? 'Cómo se organiza una ronda de NREM y REM, y cómo cambia la composición de cada ciclo a lo largo de la noche.'
      : 'How one NREM–REM round is organized, and how each cycle\'s composition shifts across the night.',
    sleepCycleContext: isEs
      ? 'Ejemplo ilustrativo — no son datos de un paciente. Las duraciones exactas varían por edad, hábitos y salud.'
      : 'Illustrative example — not patient data. Exact durations vary by age, habit, and health.',
    sleepCycleSrSummary: isEs
      ? 'Ciclo ilustrativo del sueño de aproximadamente 90 minutos: comienza con N1, pasa a N2, alcanza el sueño profundo N3, regresa por N2 y termina en REM. En los ciclos posteriores de la noche N3 disminuye mientras REM se alarga.'
      : 'Illustrative ~90-minute sleep cycle: starts in N1, deepens into N2, reaches N3 deep sleep, comes back through N2, and ends in REM. Across the night, N3 shrinks while REM lengthens in later cycles.',
    sleepCycleAxisMinutes: isEs ? 'min' : 'min',
    sleepCycleNightAxisCycle: isEs ? 'Ciclo' : 'Cycle',
    sleepCycleBandSingle: isEs ? 'Un ciclo · aprox. 90 minutos' : 'One cycle · ~90 minutes',
    sleepCycleBandNight: isEs ? 'Composición de cada ciclo durante la noche' : 'Cycle composition across the night',

    // ─── Sprint 30.9C — Circadian Ring educational visualization ─────
    circadianTitle: isEs ? 'Ejemplo ilustrativo · Ritmo circadiano de 24 h' : 'Illustrative example · 24-hour circadian rhythm',
    circadianSubtitle: isEs
      ? 'Cómo la melatonina y la alerta cambian a lo largo del día, y cómo se alinean con una ventana típica de sueño.'
      : 'How melatonin and alertness shift across the day, and how they align with a typical sleep window.',
    circadianContext: isEs
      ? 'Ejemplo ilustrativo — no son datos de un paciente. Las formas de las curvas varían por cronotipo, luz y edad.'
      : 'Illustrative example — not patient data. Curve shapes vary by chronotype, light exposure, and age.',
    circadianSrSummary: isEs
      ? 'Reloj circular de 24 horas con una franja sombreada que muestra una ventana típica de sueño entre las 23:00 y las 07:00. Una curva violeta representa la producción de melatonina, que aumenta al anochecer, alcanza su punto máximo cerca de las 03:00 y disminuye por la mañana. Una curva celeste representa la alerta, con un pico matutino y otro al anochecer y una caída por la tarde.'
      : 'A 24-hour clock ring with a shaded band showing a typical sleep window from 11 pm to 7 am. A violet curve represents melatonin production rising in the evening, peaking around 3 am, and falling by morning. A sky curve represents alertness, peaking mid-morning and early evening with an afternoon dip.',
    circadianLegendMelatonin: isEs ? 'Melatonina' : 'Melatonin',
    circadianLegendAlertness: isEs ? 'Alerta' : 'Alertness',
    circadianLegendSleepWindow: isEs ? 'Ventana típica de sueño' : 'Typical sleep window',
    circadianAxisNoon: isEs ? '↓ 12 mediodía' : '↓ 12 noon',
    circadianAxisMidnight: isEs ? '↑ 00 medianoche' : '↑ 00 midnight',

    // ─── Sprint 30.9D — PSG Signal Stack educational visualization ─────
    psgStackTitle: isEs ? 'Ejemplo ilustrativo · Canales de una PSG' : 'Illustrative example · PSG signal stack',
    psgStackSubtitle: isEs
      ? 'Qué aspecto tiene cada familia de canales durante el sueño estable: EEG, EOG, EMG y flujo aéreo.'
      : 'What each channel family typically looks like during stable sleep: EEG, EOG, EMG, and airflow.',
    psgStackContext: isEs
      ? 'Ejemplo ilustrativo — trazados sintéticos generados para enseñar la forma de la señal. No son datos de un paciente.'
      : 'Illustrative example — synthetic traces generated to teach signal shape. Not patient data.',
    psgStackSrSummary: isEs
      ? 'Cuatro trazados apilados de 30 segundos que ilustran ondas lentas del EEG, un movimiento ocular lento en el EOG, un breve estallido de actividad en el EMG y una respiración tranquila y regular en el canal de flujo aéreo.'
      : 'Four stacked 30-second traces illustrating slow-wave EEG activity, a slow eye movement in the EOG, a brief burst on the EMG, and quiet regular breathing on the airflow channel.',
    psgStackAxisSeconds: isEs ? 's' : 's',
    psgStackLaneEEG: 'EEG',
    psgStackLaneEEGSub: isEs ? 'Cuero cabelludo · cerebro' : 'Scalp · brain',
    psgStackLaneEOG: 'EOG',
    psgStackLaneEOGSub: isEs ? 'Perioculares · ojos' : 'Periocular · eyes',
    psgStackLaneEMG: 'EMG',
    psgStackLaneEMGSub: isEs ? 'Mentón · tono muscular' : 'Chin · muscle tone',
    psgStackLaneAIR: isEs ? 'Flujo' : 'Airflow',
    psgStackLaneAIRSub: isEs ? 'Nasal / oral' : 'Nasal / oral',

    // ─── Sprint 30.9E — Respiratory Event educational visualization ─────
    respEventTitle: isEs ? 'Ejemplo ilustrativo · Evento respiratorio obstructivo' : 'Illustrative example · Obstructive respiratory event',
    respEventSubtitle: isEs
      ? 'La firma clásica: el flujo aéreo se aplana, el esfuerzo continúa y la SpO₂ cae con retraso antes de recuperarse.'
      : 'The classic signature: airflow flattens, effort continues, and SpO₂ drops with a delay before recovering.',
    respEventContext: isEs
      ? 'Ejemplo ilustrativo — trazados sintéticos con fines educativos. No son datos de un paciente ni sirven para diagnóstico.'
      : 'Illustrative example — synthetic educational traces. Not patient data and not for diagnosis.',
    respEventSrSummary: isEs
      ? 'Tres trazados sincronizados. El flujo aéreo pasa de respiraciones regulares a una meseta plana durante el evento y recupera con una respiración amplia. El esfuerzo del pecho y el abdomen se mantiene durante el evento (firma obstructiva). La SpO₂ se mantiene alrededor del 97 %, cae hasta cerca del 85 % con retraso y luego se recupera tras el evento.'
      : 'Three synchronized traces. Airflow shifts from regular breaths to a flat plateau during the event and returns with a large recovery breath. Chest and abdominal effort continue throughout the event (the obstructive signature). SpO₂ holds near 97 %, drops toward ~85 % with a delay, and recovers after the event.',
    respEventLaneAirflow: isEs ? 'Flujo aéreo' : 'Airflow',
    respEventLaneAirflowSub: isEs ? 'Termistor / presión nasal' : 'Nasal thermistor / pressure',
    respEventLaneEffort: isEs ? 'Esfuerzo respiratorio' : 'Respiratory effort',
    respEventLaneEffortSub: isEs ? 'Bandas de pecho y abdomen' : 'Chest / abdomen belts',
    respEventLaneSpO2: 'SpO₂',
    respEventLaneSpO2Sub: isEs ? 'Oximetría de pulso' : 'Pulse oximeter',
    respEventEventLabel: isEs ? 'Evento' : 'Event',
    respEventAxisSeconds: isEs ? 's' : 's',

    // ─── Sprint 30.11C — Practice Coverage by Domain ─────
    practiceCoverageTitle: isEs ? 'Exposición de práctica por dominio' : 'Practice exposure by domain',
    practiceCoverageSubtitle: (programName) => (isEs
      ? `Cuántos ítems has contestado en cada dominio del programa ${programName}. Refleja exposición — no dominio, ni preparación para el examen.`
      : `How many items you have answered in each domain of the ${programName} program. Reflects exposure — not mastery, not exam readiness.`),
    practiceCoverageContext: isEs
      ? 'La exposición no es sinónimo de competencia. Los ítems contestados solo indican con qué contenidos has interactuado durante la práctica.'
      : 'Exposure is not the same as competence. Answered items only indicate which content you have interacted with during practice.',
    practiceCoverageSrSummary: (answered, total, count) => (isEs
      ? `Has contestado ${answered} de ${total} ítems disponibles en ${count} dominios.`
      : `You have answered ${answered} out of ${total} available items across ${count} domains.`),

    // ─── Sprint 30.12 — Motion Pilot scaffold ─────
    motionAssetTitle: isEs ? 'Animación educativa · Ciclo del sueño (Motion)' : 'Educational Motion · Sleep cycle',
    motionAssetSubtitle: isEs
      ? 'Complemento animado del ejemplo estático. Muestra la progresión temporal del ciclo NREM–REM.'
      : 'Animated complement to the static example. Shows the temporal progression of the NREM–REM cycle.',
    motionAssetContext: isEs
      ? 'Animación ilustrativa producida con datos sintéticos. No son datos de un paciente y no sirve para diagnóstico.'
      : 'Illustrative animation produced from synthetic data. Not patient data and not for diagnosis.',
    motionAssetSrSummary: isEs
      ? 'Animación de aproximadamente 30 segundos que muestra la progresión temporal de un ciclo del sueño desde N1 hasta REM, con subtítulos disponibles.'
      : 'Approximately 30-second animation showing the temporal progression of a sleep cycle from N1 through to REM, with captions available.',
    motionAssetPlayLabel: isEs ? 'Reproducir animación' : 'Play animation',
    motionAssetTranscriptLabel: isEs ? 'Leer transcripción' : 'Read transcript',
    motionAssetComingSoonTitle: isEs ? 'Animación · Próximamente' : 'Motion Asset · Coming Soon',
    motionAssetComingSoonBody: isEs
      ? 'Estamos produciendo una animación educativa que complementa el ejemplo estático de arriba. Aparecerá aquí cuando la revisión científica y las subtítulos bilingües estén listos.'
      : 'We are producing an educational animation to complement the static example above. It will appear here once scientific review and bilingual captions are complete.',
    motionAssetComingSoonFallbackNote: isEs
      ? 'Mientras tanto, el ejemplo estático de arriba enseña el mismo concepto por sí solo.'
      : 'In the meantime, the static example above teaches the same concept on its own.',
    motionAssetCaptionsUnavailable: isEs
      ? 'Los subtítulos bilingües aún no están disponibles para esta animación — la reproducción está deshabilitada.'
      : 'Bilingual captions are not yet available for this Motion asset — playback is disabled.',

    // ─── Sprint 30.13A — Central Apnea Event ─────
    centralApneaTitle: isEs ? 'Ejemplo ilustrativo · Evento respiratorio central' : 'Illustrative example · Central respiratory event',
    centralApneaSubtitle: isEs
      ? 'La firma central: el flujo aéreo se detiene y el esfuerzo también cesa. Distinta a la firma obstructiva.'
      : 'The central signature: airflow stops and effort also ceases. Distinct from the obstructive signature.',
    centralApneaContext: isEs
      ? 'Ejemplo ilustrativo — trazados sintéticos con fines educativos. No son datos de un paciente ni sirve para diagnóstico.'
      : 'Illustrative example — synthetic educational traces. Not patient data and not for diagnosis.',
    centralApneaSrSummary: isEs
      ? 'Tres trazados sincronizados. El flujo aéreo se aplana durante el evento. El esfuerzo de pecho y abdomen también cae hasta casi cero durante el evento (firma central). La SpO₂ desciende con retraso y luego se recupera. Una tarjeta de contraste muestra las diferencias con la firma obstructiva.'
      : 'Three synchronized traces. Airflow flattens during the event. Chest and abdominal effort also drops to near-zero during the event (central signature). SpO₂ descends with a delay and recovers. A contrast card shows the differences from the obstructive signature.',
    centralApneaEventLabel: isEs ? 'Evento central' : 'Central event',
    centralApneaContrastTitle: isEs ? 'Central vs Obstructivo · en un vistazo' : 'Central vs Obstructive · at a glance',
    centralApneaContrastCentralLabel: isEs ? 'Central' : 'Central',
    centralApneaContrastObstructiveLabel: isEs ? 'Obstructivo' : 'Obstructive',
    centralApneaContrastCentralNote: isEs
      ? 'El flujo y el esfuerzo caen al mismo tiempo durante el evento.'
      : 'Airflow and effort both drop during the event.',
    centralApneaContrastObstructiveNote: isEs
      ? 'El flujo cae pero el esfuerzo persiste (paradójico).'
      : 'Airflow drops but effort persists (paradoxical).',

    // ─── Sprint 30.13B — Process S / Process C ─────
    processScTitle: isEs ? 'Ejemplo ilustrativo · Modelo de dos procesos' : 'Illustrative example · Two-process model of sleep',
    processScSubtitle: isEs
      ? 'Proceso S (presión homeostática) y Proceso C (ritmo circadiano) a lo largo de 24 horas.'
      : 'Process S (homeostatic sleep pressure) and Process C (circadian rhythm) across 24 hours.',
    processScContext: isEs
      ? 'Ejemplo ilustrativo — no son datos de un paciente. Las formas exactas de las curvas varían por edad, hábitos y cronotipo.'
      : 'Illustrative example — not patient data. Exact curve shapes vary by age, habit, and chronotype.',
    processScSrSummary: isEs
      ? 'Un gráfico de 24 horas muestra dos curvas. La curva violeta (Proceso S) sube durante la vigilia y baja durante el sueño. La curva celeste (Proceso C) es una oscilación circadiana con un pico matutino, un descenso vespertino y un mínimo en la madrugada. Una franja sombreada indica la ventana típica de sueño.'
      : 'A 24-hour chart shows two curves. The violet curve (Process S) rises during wake and falls during sleep. The sky curve (Process C) is a circadian oscillation with a morning peak, afternoon dip, and early-morning nadir. A shaded band marks the typical sleep window.',
    processScLegendS: isEs ? 'Proceso S · presión de sueño (homeostática)' : 'Process S · sleep pressure (homeostatic)',
    processScLegendC: isEs ? 'Proceso C · impulso circadiano de alerta' : 'Process C · circadian alerting drive',
    processScLegendSleepWindow: isEs ? 'Ventana típica de sueño' : 'Typical sleep window',
    processScAxisTime: isEs ? 'Hora del día (hora del reloj)' : 'Time of day (clock hour)',
    processScAxisLevelLow: isEs ? 'bajo' : 'low',
    processScAxisLevelHigh: isEs ? 'alto' : 'high',
    processScWakeLabel: isEs ? 'Vigilia' : 'Wake',
    processScSleepLabel: isEs ? 'Sueño' : 'Sleep',

    // ─── Practice / certification mapping badge (iter168) ─────────
    practiceMappingTitle: isEs ? 'Preparación de examen' : 'Certification preparation',
    practiceMappingCountLabel: (n) => {
      const count = Number(n) || 0;
      return isEs
        ? (count === 1 ? '1 pregunta de práctica mapeada' : `${count} preguntas de práctica mapeadas`)
        : (count === 1 ? '1 mapped practice question' : `${count} mapped practice questions`);
    },
    practiceMappingCertsLabel: isEs ? 'Rutas de certificación' : 'Certification paths',
    practiceMappingDomainsLabel: isEs ? 'Dominios de práctica' : 'Practice domains',
    practiceMappingNoteInherited: isEs
      ? 'La correspondencia con la práctica se hereda del módulo. La cobertura no es una predicción de aprobar el examen.'
      : 'The practice mapping is inherited from the module. Coverage is not a pass prediction.',
    practiceMappingCta: isEs ? 'Ir a la práctica' : 'Go to practice',
    practiceMappingUnmapped: isEs
      ? 'Sin preguntas de práctica mapeadas todavía para esta lección.'
      : 'No practice questions are mapped to this lesson yet.',

    videoComingSoonTitle: isEs ? 'Video próximamente' : 'Video coming soon',
    videoComingSoonBody: isEs
      ? 'La lección visual está disponible abajo mientras producimos el video de esta lección.'
      : 'The visual lesson is available below while this lesson\'s video is being produced.',
    videoComingSoonCta: isEs ? 'Ver la lección visual' : 'View the visual lesson',

    // ─── M7-M12 Final Completion Wave (2026-02-12) ─────────────────
    // Six Founder-approved educational visualizations.
    // All strings are non-diagnostic, educational, and clearly labeled
    // as illustrative synthetic examples.

    // ─── ScoringEpoch (L201 + L204) ────────────────────────────────
    scoringEpochTitle: isEs
      ? 'Ejemplo ilustrativo · Información que aporta contexto a una época'
      : 'Illustrative example · Information that gives context to an epoch',
    scoringEpochSubtitle: isEs
      ? 'Qué canales aportan información a una decisión de estadificación. Objetivo educativo — no es una guía de puntuación.'
      : 'Which channels contribute information to a staging decision. Educational aim — this is not a scoring guide.',
    scoringEpochContext: isEs
      ? 'Ejemplo ilustrativo — trazados sintéticos generados para enseñar la forma de la señal. No son datos de un paciente.'
      : 'Illustrative example — synthetic traces generated to teach signal shape. Not patient data.',
    scoringEpochSrSummary: isEs
      ? 'Ventana educativa de treinta segundos con tres carriles apilados —EEG, EOG y EMG del mentón— que muestran patrones ilustrativos de una época típica. Cada carril aporta información distinta a la decisión de estadificación; ningún carril por sí solo la determina.'
      : 'Educational thirty-second window with three stacked lanes — EEG, EOG, and chin EMG — showing illustrative patterns for a typical epoch. Each lane contributes distinct information to the staging decision; no single lane determines it.',
    scoringEpochAxisSeconds: 's',
    scoringEpochLaneEEG: 'EEG',
    scoringEpochLaneEEGSub: isEs ? 'Actividad cerebral' : 'Brain activity',
    scoringEpochLaneEOG: 'EOG',
    scoringEpochLaneEOGSub: isEs ? 'Movimientos oculares' : 'Eye movements',
    scoringEpochLaneEMG: 'EMG',
    scoringEpochLaneEMGSub: isEs ? 'Tono muscular · mentón' : 'Chin muscle tone',
    scoringEpochContextStripTitle: isEs
      ? 'Qué observar en cada carril'
      : 'What to notice in each lane',
    scoringEpochContextStripEEGNote: isEs
      ? 'Los patrones lentos y las oscilaciones más rápidas aportan pistas sobre la profundidad del sueño.'
      : 'Slow patterns and faster oscillations give clues about sleep depth.',
    scoringEpochContextStripEOGNote: isEs
      ? 'Los movimientos oculares (lentos o rápidos) ayudan a distinguir la transición y REM.'
      : 'Eye movements (slow or rapid) help distinguish transition and REM.',
    scoringEpochContextStripEMGNote: isEs
      ? 'El tono del mentón cambia entre estados y aporta información complementaria.'
      : 'Chin tone changes between states and adds complementary information.',
    scoringEpochContextStripFooter: isEs
      ? 'La época se interpreta en conjunto — no como canales aislados. Esta lección introduce el concepto, no aplica reglas.'
      : 'The epoch is read together — not as isolated channels. This lesson introduces the concept, it does not apply rules.',

    // ─── ArtifactMicroCards (L225) ──────────────────────────────────
    artifactCardsTitle: isEs
      ? 'Ejemplo ilustrativo · Familias educativas de artefactos'
      : 'Illustrative example · Educational artifact families',
    artifactCardsSubtitle: isEs
      ? 'Cinco familias didácticas que ayudan a reconocer patrones sospechosos. Marco educativo — no una taxonomía oficial exhaustiva.'
      : 'Five teaching families that help recognize suspicious patterns. Educational framework — not an exhaustive official taxonomy.',
    artifactCardsContext: isEs
      ? 'Ejemplos ilustrativos — trazados sintéticos generados para enseñar. No son datos de un paciente.'
      : 'Illustrative examples — synthetic traces generated for teaching. Not patient data.',
    artifactCardsSrSummary: isEs
      ? 'Cinco tarjetas educativas con formas de onda sintéticas: movimiento, electrodo/sensor, ambiente/eléctrico, sudor/deriva de base, y pérdida/desplazamiento de la señal. Cada tarjeta describe qué hace sospechoso al patrón, con qué otras señales conviene compararlo, y una conclusión educativa segura.'
      : 'Five educational cards with synthetic waveforms: movement, electrode/sensor, environmental/electrical, sweat/baseline drift, and signal loss/displacement. Each card describes what makes the pattern suspicious, which other signals to compare against, and a safe educational takeaway.',
    artifactCardsFrameworkNote: isEs
      ? 'Este marco de cinco familias es una herramienta de enseñanza del SHS; no se presenta como una taxonomía oficial exhaustiva.'
      : 'This five-family framework is an SHS teaching tool; it is not presented as an exhaustive official taxonomy.',
    artifactCardsPatternLabel: isEs ? 'Qué lo hace sospechoso' : 'What makes it suspicious',
    artifactCardsCompareLabel: isEs ? 'Con qué comparar' : 'What to compare',
    artifactCardsTakeawayLabel: isEs ? 'Conclusión educativa' : 'Educational takeaway',
    artifactCardsFamilyMovementName: isEs ? 'Relacionado con movimiento' : 'Movement-related',
    artifactCardsFamilyMovementPattern: isEs
      ? 'Deflexiones grandes e irregulares en varios canales al mismo tiempo.'
      : 'Large, irregular deflections appearing across several channels at once.',
    artifactCardsFamilyMovementCompare: isEs
      ? 'Otros canales y notas de posición del técnico durante la grabación.'
      : 'Other channels and technologist position notes during the recording.',
    artifactCardsFamilyMovementTakeaway: isEs
      ? 'El movimiento suele afectar varios canales; verifica el contexto antes de interpretar.'
      : 'Movement usually affects several channels; check the context before interpreting.',
    artifactCardsFamilyElectrodeName: isEs ? 'Electrodo / sensor' : 'Electrode / sensor',
    artifactCardsFamilyElectrodePattern: isEs
      ? 'Cambio brusco de nivel de base o "pop" limitado a un solo canal.'
      : 'Sudden step in baseline level or a "pop" limited to one channel.',
    artifactCardsFamilyElectrodeCompare: isEs
      ? 'El mismo canal antes/después y los canales vecinos que no cambiaron.'
      : 'The same channel before/after and neighboring channels that did not change.',
    artifactCardsFamilyElectrodeTakeaway: isEs
      ? 'Cuando un solo canal cambia, suele haber un problema físico del sensor.'
      : 'When just one channel changes, a physical sensor issue is often the cause.',
    artifactCardsFamilyEnvironmentalName: isEs ? 'Ambiental / eléctrico' : 'Environmental / electrical',
    artifactCardsFamilyEnvironmentalPattern: isEs
      ? 'Oscilación rápida y muy regular superpuesta a la señal.'
      : 'Fast, very regular oscillation layered on top of the signal.',
    artifactCardsFamilyEnvironmentalCompare: isEs
      ? 'Otros canales y el entorno eléctrico de la sala.'
      : 'Other channels and the electrical environment in the room.',
    artifactCardsFamilyEnvironmentalTakeaway: isEs
      ? 'Los patrones muy regulares suelen provenir del ambiente, no del paciente.'
      : 'Very regular patterns often come from the environment, not the patient.',
    artifactCardsFamilySweatName: isEs ? 'Sudor / deriva de base' : 'Sweat / baseline drift',
    artifactCardsFamilySweatPattern: isEs
      ? 'Deriva muy lenta y sostenida de la línea de base.'
      : 'Very slow, sustained wandering of the baseline.',
    artifactCardsFamilySweatCompare: isEs
      ? 'Temperatura de la sala y notas del técnico.'
      : 'Room temperature and technologist notes.',
    artifactCardsFamilySweatTakeaway: isEs
      ? 'La deriva lenta puede simular ondas fisiológicas; verifica el contexto físico.'
      : 'Slow drift can mimic physiologic waves; verify the physical context.',
    artifactCardsFamilyLossName: isEs ? 'Pérdida / desplazamiento de señal' : 'Signal loss / displacement',
    artifactCardsFamilyLossPattern: isEs
      ? 'Trazo plano o interrupción abrupta durante un intervalo.'
      : 'Flat trace or abrupt interruption during an interval.',
    artifactCardsFamilyLossCompare: isEs
      ? 'Estado del sensor y del cable, y otros canales durante el mismo intervalo.'
      : 'Sensor and cable status, and other channels during the same interval.',
    artifactCardsFamilyLossTakeaway: isEs
      ? 'Una señal ausente no se puede puntuar; regístralo y revisa el equipo.'
      : 'A missing signal cannot be scored; document it and review the equipment.',

    // ─── PAPFlowDiagram (L210 + L211) ───────────────────────────────
    papFlowTitle: isEs
      ? 'Ejemplo ilustrativo · Modos de presión positiva'
      : 'Illustrative example · Positive-airway-pressure modes',
    papFlowSubtitle: isEs
      ? 'Comparación conceptual de la forma de la presión en CPAP, APAP y BPAP.'
      : 'Conceptual comparison of pressure shape in CPAP, APAP, and BPAP.',
    papFlowContext: isEs
      ? 'Ejemplos ilustrativos — sin valores numéricos, sin recomendaciones de titulación, sin marcas comerciales.'
      : 'Illustrative examples — no numeric values, no titration recommendations, no vendor branding.',
    papFlowSrSummary: isEs
      ? 'Tres pequeños diagramas conceptuales de presión sin valores numéricos: CPAP muestra una línea constante; APAP muestra un ajuste gradual; BPAP alterna entre una presión inspiratoria mayor y una espiratoria menor durante cada respiración.'
      : 'Three small conceptual pressure diagrams with no numeric values: CPAP shows a constant line; APAP shows a gradual adjustment; BPAP alternates between a higher inspiratory and lower expiratory pressure with each breath.',
    papFlowEducationalNote: isEs
      ? 'Educativo · no de titulación. Ningún ajuste, dispositivo o tratamiento se recomienda aquí.'
      : 'Educational · not for titration. No settings, devices, or treatments are recommended here.',
    papFlowAxisTime: isEs ? 'tiempo (respiraciones)' : 'time (breaths)',
    papFlowPressureAxisLabel: isEs ? 'presión (concepto)' : 'pressure (concept)',
    papFlowHigherLabel: isEs ? 'mayor' : 'higher',
    papFlowLowerLabel: isEs ? 'menor' : 'lower',
    papFlowModeCPAPName: 'CPAP',
    papFlowModeCPAPTag: isEs ? 'presión continua' : 'continuous',
    papFlowModeCPAPDesc: isEs
      ? 'La presión permanece constante a lo largo del ciclo respiratorio.'
      : 'Pressure remains constant across the breathing cycle.',
    papFlowModeAPAPName: 'APAP',
    papFlowModeAPAPTag: isEs ? 'presión adaptativa' : 'adaptive',
    papFlowModeAPAPDesc: isEs
      ? 'La presión se ajusta gradualmente en respuesta a la información del dispositivo.'
      : 'Pressure adjusts gradually in response to device-derived information.',
    papFlowModeBPAPName: 'BPAP',
    papFlowModeBPAPTag: isEs ? 'dos niveles' : 'two levels',
    papFlowModeBPAPDesc: isEs
      ? 'Se alternan una presión inspiratoria mayor y una espiratoria menor con cada respiración.'
      : 'A higher inspiratory pressure and a lower expiratory pressure alternate with each breath.',

    // ─── EventFamilyOverview (L205) ─────────────────────────────────
    eventFamilyTitle: isEs
      ? 'Ejemplo ilustrativo · Familias de eventos'
      : 'Illustrative example · Event families',
    eventFamilySubtitle: isEs
      ? 'Cómo se agrupan conceptualmente los eventos y qué señales suelen informarlos. No es un manual de puntuación.'
      : 'How events are grouped conceptually and which signals typically inform them. This is not a scoring manual.',
    eventFamilyContext: isEs
      ? 'Ejemplos ilustrativos — sin duraciones, porcentajes ni umbrales universales.'
      : 'Illustrative examples — no durations, percentages, or universal thresholds.',
    eventFamilySrSummary: isEs
      ? 'Cuatro tarjetas de familias de eventos: respiratorios, arousals, movimientos y cardíacos. Cada tarjeta enumera los canales de señal comúnmente asociados a esa familia.'
      : 'Four event-family tiles: respiratory, arousals, movement, and cardiac. Each tile lists the signal channels commonly associated with that family.',
    eventFamilyNonScoringNote: isEs
      ? 'Esta vista general no sustituye reglas de puntuación ni el manual vigente.'
      : 'This overview does not replace scoring rules or the current manual.',
    eventFamilySignalsLabel: isEs ? 'Señales comúnmente involucradas' : 'Signals commonly involved',
    eventFamilyRespiratoryName: isEs ? 'Respiratorios' : 'Respiratory',
    eventFamilyRespiratoryNote: isEs
      ? 'Eventos relacionados con el flujo de aire, el esfuerzo respiratorio y la oxigenación.'
      : 'Events related to airflow, respiratory effort, and oxygenation.',
    eventFamilyArousalsName: isEs ? 'Arousals (activaciones)' : 'Arousals',
    eventFamilyArousalsNote: isEs
      ? 'Cambios breves y significativos observables principalmente en la actividad cortical.'
      : 'Brief, significant changes observed primarily in cortical activity.',
    eventFamilyMovementName: isEs ? 'Movimientos' : 'Movement',
    eventFamilyMovementNote: isEs
      ? 'Eventos motores repetitivos o aislados detectados por canales musculares.'
      : 'Repetitive or isolated motor events detected on muscle channels.',
    eventFamilyCardiacName: isEs ? 'Cardíacos' : 'Cardiac',
    eventFamilyCardiacNote: isEs
      ? 'Cambios de frecuencia o ritmo observados en el ECG y su relación con la oxigenación.'
      : 'Rate or rhythm changes observed on the ECG and their relationship to oxygenation.',
    eventFamilySignalEEG: 'EEG',
    eventFamilySignalEOG: 'EOG',
    eventFamilySignalEMG: 'EMG',
    eventFamilySignalAirflow: isEs ? 'Flujo aéreo' : 'Airflow',
    eventFamilySignalEffort: isEs ? 'Esfuerzo' : 'Effort',
    eventFamilySignalSpO2: 'SpO₂',
    eventFamilySignalECG: 'ECG',

    // ─── ScoringWorkflowDiagram (L206) ──────────────────────────────
    scoringWorkflowTitle: isEs
      ? 'Ejemplo ilustrativo · Flujo educativo de puntuación'
      : 'Illustrative example · Educational scoring workflow',
    scoringWorkflowSubtitle: isEs
      ? 'Cómo se organiza conceptualmente el trabajo de puntuación: secuencia, revisión, documentación y calidad.'
      : 'How scoring work is organized conceptually: sequence, review, documentation, and quality.',
    scoringWorkflowContext: isEs
      ? 'Ejemplo ilustrativo — no representa el SOP obligatorio de ningún laboratorio en particular.'
      : 'Illustrative example — does not represent the mandatory SOP of any particular laboratory.',
    scoringWorkflowSrSummary: isEs
      ? 'Diagrama educativo de cinco pasos conectados con flechas: adquirir señales, verificar calidad, puntuar por reglas, revisión por pares o control de calidad, y documentar y escalar.'
      : 'Educational five-step diagram connected by arrows: acquire signals, verify quality, score by rules, peer or QA review, and document and escalate.',
    scoringWorkflowScopeNote: isEs
      ? 'La secuencia y la nomenclatura exactas pueden variar según la institución y las políticas locales vigentes.'
      : 'Exact sequence and terminology may vary by institution and current local policy.',
    scoringWorkflowNodeAcquireName: isEs ? 'Adquirir' : 'Acquire',
    scoringWorkflowNodeAcquireBlurb: isEs ? 'Registro y montaje.' : 'Recording & montage.',
    scoringWorkflowNodeVerifyName: isEs ? 'Verificar' : 'Verify',
    scoringWorkflowNodeVerifyBlurb: isEs ? 'Calidad de señal.' : 'Signal quality.',
    scoringWorkflowNodeScoreName: isEs ? 'Puntuar' : 'Score',
    scoringWorkflowNodeScoreBlurb: isEs ? 'Reglas vigentes.' : 'Current rules.',
    scoringWorkflowNodeReviewName: isEs ? 'Revisar' : 'Review',
    scoringWorkflowNodeReviewBlurb: isEs ? 'Pares · QC.' : 'Peer · QC.',
    scoringWorkflowNodeDocumentName: isEs ? 'Documentar' : 'Document',
    scoringWorkflowNodeDocumentBlurb: isEs ? 'Reporte · escalado.' : 'Report · escalate.',
    scoringWorkflowScoringVsInterpLabel: isEs ? 'Puntuación vs. interpretación' : 'Scoring vs. interpretation',
    scoringWorkflowScoringVsInterpNote: isEs
      ? 'La puntuación es un paso técnico realizado por profesionales acreditados. La interpretación clínica integra la historia médica y es responsabilidad del médico. Este material no reemplaza la responsabilidad clínica.'
      : 'Scoring is a technical step performed by credentialed professionals. Clinical interpretation integrates medical history and is the physician\'s responsibility. This material does not replace clinical responsibility.',

    // ─── InterScorerComparisonMicroVisual (L207) ───────────────────
    interScorerTitle: isEs
      ? 'Ejemplo ilustrativo · Comparación entre puntuadores'
      : 'Illustrative example · Inter-scorer comparison',
    interScorerSubtitle: isEs
      ? 'Cómo la consistencia y la variabilidad se hacen visibles al comparar decisiones. Enfoque de mejora de calidad, no de "puntuación correcta única".'
      : 'How consistency and variability become visible when decisions are compared. Quality-improvement lens, not a single-"correct-score" lens.',
    interScorerContext: isEs
      ? 'Ejemplo ilustrativo — épocas sintéticas y decisiones simuladas para enseñar. No son datos reales de puntuadores.'
      : 'Illustrative example — synthetic epochs and simulated decisions for teaching. Not real scorer data.',
    interScorerSrSummary: isEs
      ? 'Tres carriles ilustrativos (Puntuador A, B y C) con doce épocas cada uno. Las épocas donde los tres coinciden se muestran sin contorno; aquellas donde al menos un puntuador difiere aparecen resaltadas con un contorno discontinuo.'
      : 'Three illustrative lanes (Scorer A, B, and C) with twelve epochs each. Epochs where all three agree are shown without an outline; those where at least one scorer differs are highlighted with a dashed outline.',
    interScorerNoTargetNote: isEs
      ? 'No se propone aquí un porcentaje objetivo de concordancia. Los programas de calidad locales definen sus propios criterios.'
      : 'No target percentage agreement is proposed here. Local quality programs define their own criteria.',
    interScorerScorerLabel: isEs ? 'Puntuador' : 'Scorer',
    interScorerEpochLabel: isEs ? 'Época' : 'Epoch',
    interScorerConcordantLabel: isEs ? 'Todos los puntuadores coinciden' : 'All scorers agree',
    interScorerDiscordantLabel: isEs ? 'Al menos uno difiere' : 'At least one differs',
    interScorerStageW: isEs ? 'V' : 'W',
    interScorerStageN1: 'N1',
    interScorerStageN2: 'N2',
    interScorerStageN3: 'N3',
    interScorerStageREM: 'R',
  };
};

export const formatSeconds = (sec) => {
  if (!sec || Number.isNaN(sec)) return '—';
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s ? `${m}m ${s}s` : `${m}m`;
};

export const formatMinutes = (sec) => {
  if (!sec || Number.isNaN(sec)) return 0;
  return Math.max(1, Math.round(sec / 60));
};

/**
 * Pick a time-of-day greeting for the "Welcome back" banner.
 * Deliberately UTC-agnostic — uses the browser's local hour.
 */
export const getGreeting = (language) => {
  const copy = getAcademyCopy(language);
  const h = new Date().getHours();
  if (h < 12) return copy.welcomeGoodMorning;
  if (h < 18) return copy.welcomeGoodAfternoon;
  return copy.welcomeGoodEvening;
};
